"use client"

import * as React from "react"
import { ContextMenu, ContextMenuContent, ContextMenuPortal, ContextMenuTrigger } from "../shadcn/context-menu"

interface TContextMenuProps {
	/** 우클릭 대상 영역. ContextMenuTrigger 내부에 렌더링된다 */
	children: React.ReactNode
	/**
	 * 메뉴 콘텐츠 렌더러.
	 * onClose는 onMenuClose 콜백만 호출하며 Radix 메뉴 자체의 닫힘은 트리거하지 못한다
	 * (open은 Radix 이벤트로만 바뀌고 이 함수에서 닫지 않는다).
	 * 메뉴 아이템 onSelect는 Radix가 자동으로 메뉴를 닫으므로 대부분의 경우 onClose는 필요 없다.
	 * 외부 상태 동기화 목적으로만 사용하라.
	 */
	renderMenu: (props: { onClose: () => void }) => React.ReactNode
	/** 메뉴 열림 콜백 */
	onMenuOpen?: () => void
	/** 메뉴 닫힘 콜백 */
	onMenuClose?: () => void
	/**
	 * Portal 사용 여부. 기본 true.
	 * false인 경우 parent stacking context에 갇히므로 z-index가 보장되지 않는다.
	 * 부모가 `overflow: hidden`이거나 낮은 z-index 컨테이너 내부면 반드시 true로 둬라.
	 */
	portal?: boolean
	/** Content className override */
	className?: string
}

// 터치·펜 pointerdown을 안쪽이 먼저 가져갔는지 표시한다.
// Radix는 중첩 trigger가 롱프레스를 양보하지 않아 바깥 메뉴도 함께 열린다.
// 업스트림 수정(radix-ui/primitives#4199)이 설치 버전에 반영되면 TContextMenu 간 claim은 제거할 수 있다.
// 단 claimContextMenuPointerDown은 trigger가 아닌 요소(트랙 라벨)용이라 업스트림이 대신하지 못하므로 남는다.
const claimedPointerDowns = new WeakSet<Event>()

/**
 * 이 pointerdown은 바깥 TContextMenu가 열지 않아야 하는 영역에서 처리됐다고 표시한다.
 * 마우스는 영역의 onContextMenu preventDefault로 바깥 메뉴가 건너뛰어지지만,
 * 터치·펜은 pointerdown 타이머로 열려 contextmenu를 기다리지 않으므로 영역의 onPointerDown에서 부른다.
 */
export function claimContextMenuPointerDown(e: React.PointerEvent) {
	if (e.pointerType !== "mouse") claimedPointerDowns.add(e.nativeEvent)
}

export const TContextMenu = React.memo((props: TContextMenuProps) => {
	const { children, renderMenu, onMenuOpen, onMenuClose, portal = true, className } = props

	const [open, setOpen] = React.useState(false)
	// 이번 제스처를 안쪽 trigger가 가져갔으면 true. 가져간 쪽이 아닌 trigger의 열림 요청은 거절한다.
	const claimedRef = React.useRef(false)

	// 이벤트는 바꾸지 않고(preventDefault·stopPropagation 없음) 바깥 trigger의 타이머가 걸려도 open만 거절한다.
	const handlePointerDown = React.useCallback((e: React.PointerEvent) => {
		claimedRef.current = e.pointerType !== "mouse" && claimedPointerDowns.has(e.nativeEvent)
		claimContextMenuPointerDown(e)
	}, [])

	// 키보드·플랫폼 contextmenu처럼 pointerdown 없이 여는 경로는 claim과 무관하게 연다.
	const handleContextMenu = React.useCallback((e: React.MouseEvent) => {
		if (!e.defaultPrevented) claimedRef.current = false
	}, [])

	const onOpenChange = React.useCallback(
		(next: boolean) => {
			if (next && claimedRef.current) return
			setOpen(next)
			if (next) {
				onMenuOpen?.()
			} else {
				onMenuClose?.()
			}
		},
		[onMenuOpen, onMenuClose]
	)

	// onClose는 외부 콜백 호출만 담당한다. 실제 닫힘은 Radix가 처리한다.
	const handleClose = React.useCallback(() => {
		onMenuClose?.()
	}, [onMenuClose])

	const content = <ContextMenuContent className={className}>{renderMenu({ onClose: handleClose })}</ContextMenuContent>

	return (
		<ContextMenu open={open} onOpenChange={onOpenChange}>
			<ContextMenuTrigger asChild onPointerDown={handlePointerDown} onContextMenu={handleContextMenu}>
				{children}
			</ContextMenuTrigger>
			{portal ? <ContextMenuPortal>{content}</ContextMenuPortal> : content}
		</ContextMenu>
	)
})

TContextMenu.displayName = "TContextMenu"
